import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Box, BoxProps, Grid, Skeleton, debounce } from '@mui/material'
import { useWindowVirtualizer, VirtualItem } from '@tanstack/react-virtual'

import './WindowVirtualizedList.scss'
import { ResponsiveColumns, useResponsiveColumns } from './hooks/useResponsiveColumns'

export type { ResponsiveColumns }

/**
 * Props for the WindowVirtualizedList component
 *
 * @template T - The type of items in the list
 */
export type WindowVirtualizedListProps<T> = {
  /**
   * Array of items to render in the virtualized list. Defaults to [] when omitted.
   */
  items: T[]
  /**
   * Function to render each item. Receives the item and its index.
   * @param item - The item to render
   * @param index - The index of the item in the array
   * @returns ReactNode to render for this item
   */
  renderItem: (item: T, index: number) => ReactNode
  /**
   * Number of columns to display. Can be a fixed number or responsive object.
   * When > 1, items are grouped into rows and virtualized by row.
   * @example columns={2} // Always 2 columns
   * @example columns={{ xs: 1, md: 2, lg: 3 }} // Responsive
   */
  columns?: number | ResponsiveColumns
  /**
   * Estimated height of each row in pixels. Used for initial calculations.
   * Defaults to 100px if not provided.
   */
  estimateSize?: number
  /**
   * Number of rows to render outside the visible viewport for smoother scrolling.
   * Defaults to 5.
   */
  overscan?: number
  /**
   * Gap between rows in pixels. Defaults to 0.
   * For Grid columnSpacing, uses theme spacing (default 8px per unit); gap/8 is used as theme units.
   */
  gap?: number
  /**
   * Whether the list is currently loading data.
   * When true, shows the loadingComponent instead of items.
   */
  isLoading?: boolean
  /**
   * Custom component to show when isLoading is true.
   * Defaults to a centered CircularProgress.
   */
  loadingComponent?: ReactNode
  /**
   * Label or component to show when items array is empty.
   * Defaults to null (nothing shown).
   */
  noItemsLabel?: ReactNode
  /**
   * Function to generate a unique key for each item.
   * If not provided, uses the item's index.
   * @param item - The item
   * @param index - The index of the item
   * @returns A unique key (string or number)
   */
  getItemKey?: (item: T, index: number) => string | number
  /**
   * Material UI Box props to apply to the container element.
   * Useful for styling, spacing, etc.
   */
  containerProps?: BoxProps
  /**
   * Optional custom row wrapper. Receives the row cells (as ReactNode[]) and row index.
   * When provided, the list uses this instead of the default Grid container for each row.
   * Useful for custom layouts (e.g. flex, custom grid) without forking the component.
   */
  renderRow?: (rowNodes: ReactNode[], rowIndex: number) => ReactNode
}

/**
 * A simplified virtualized list component that efficiently renders large lists
 * using window-based scrolling with @tanstack/react-virtual.
 *
 * This component uses useWindowVirtualizer for window scrolling and supports
 * multi-column layouts using MUI Grid2. Items are grouped into rows when
 * multiple columns are specified, and each row is virtualized.
 *
 * @template T - The type of items in the list
 *
 * @example
 * // Simple list with string items
 * <WindowVirtualizedList
 *   items={['Item 1', 'Item 2', 'Item 3']}
 *   renderItem={(item) => <div>{item}</div>}
 *   estimateSize={50}
 * />
 *
 * @example
 * // Multi-column layout with responsive columns
 * <WindowVirtualizedList
 *   items={featureBreakdowns}
 *   columns={{ xs: 1, lg: 2 }}
 *   estimateSize={350}
 *   overscan={3}
 *   gap={16}
 *   getItemKey={(fb) => fb.id}
 *   renderItem={(featureBreakdown) => (
 *     <FeatureBreakdownCard featureBreakdown={featureBreakdown} />
 *   )}
 *   noItemsLabel={<Trans i18nKey="common:no_items" />}
 * />
 */
export const WindowVirtualizedList = <T,>({
  items = [],
  renderItem,
  columns,
  estimateSize = 100,
  overscan = 5,
  gap = 0,
  isLoading = false,
  loadingComponent,
  noItemsLabel,
  getItemKey,
  containerProps,
  renderRow,
}: WindowVirtualizedListProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollMargin, setScrollMargin] = useState(0)

  const columnCount = useResponsiveColumns(columns)

  // Chunk items into rows when columns > 1
  const rows = useMemo(() => {
    if (columnCount <= 1) {
      return items.map((item) => [item])
    }
    const result: T[][] = []
    for (let i = 0; i < items.length; i += columnCount) {
      result.push(items.slice(i, i + columnCount))
    }
    return result
  }, [items, columnCount])

  // Document-relative top of the container (correct regardless of offsetParent).
  // Rows are absolutely positioned at `virtualRow.start - scrollMargin`, so a stale
  // value paints every row at the wrong offset.
  const syncScrollMargin = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const next = el.getBoundingClientRect().top + window.scrollY
    // Ignore sub-pixel drift, which would otherwise re-render the list on every
    // observer callback without moving anything.
    setScrollMargin((current) => (Math.abs(current - next) < 1 ? current : next))
  }, [])

  // Calculate scroll margin on mount (useLayoutEffect for accurate timing)
  useLayoutEffect(() => {
    syncScrollMargin()
  }, [syncScrollMargin])

  // The container's document offset also moves when layout around it changes: a
  // sibling row expanding, a Collapse animating open, a lazily loaded section
  // arriving. None of those fire scroll or resize, so watch layout itself rather
  // than waiting for an input event. Without this, a list that mounts or moves
  // mid-layout keeps its original margin forever and its rows are drawn on top of
  // whatever follows the list — worst for a list nested inside an expandable row,
  // which mounts while its parent is still animating open.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => syncScrollMargin())
    observer.observe(document.body)
    return () => observer.disconnect()
  }, [syncScrollMargin])

  // Debounced handler to recalc scroll margin (resize + scroll)
  const updateScrollMarginRef = useRef(debounce(syncScrollMargin, 150))

  useEffect(() => {
    const updateScrollMargin = updateScrollMarginRef.current
    window.addEventListener('resize', updateScrollMargin)
    window.addEventListener('scroll', updateScrollMargin, { passive: true })
    return () => {
      window.removeEventListener('resize', updateScrollMargin)
      window.removeEventListener('scroll', updateScrollMargin)
      updateScrollMargin.clear()
    }
  }, [])

  // The virtualizer caches measured heights by item key, so handing it the caller's
  // key makes a row keep its height when the list is re-sorted, filtered or refetched.
  // Left to the default the cache is keyed by index, and a changed list attributes
  // heights to whichever item now sits at that index.
  const getVirtualRowKey = useCallback(
    (rowIndex: number): string | number => {
      const firstItem = rows[rowIndex]?.[0]
      if (firstItem == null || !getItemKey) return rowIndex
      return getItemKey(firstItem, rowIndex * columnCount)
    },
    [rows, columnCount, getItemKey]
  )

  // Setup window virtualizer
  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => estimateSize,
    overscan,
    scrollMargin,
    gap,
    getItemKey: getVirtualRowKey,
  })

  // Render loading state: skeleton grid matching configured columns, 3 rows
  if (isLoading) {
    const skeletonRows = 3
    return (
      <Box className="WindowVirtualizedList" ref={containerRef} {...containerProps}>
        {loadingComponent ?? (
          <Grid container direction="column" rowSpacing={gap / 8}>
            {Array.from({ length: skeletonRows }).map((_, rowIndex) => (
              <Grid item key={`skeleton-row-${rowIndex}`} xs={12}>
                <Grid container columnSpacing={gap / 8}>
                  {Array.from({ length: columnCount }).map((_, colIndex) => (
                    <Grid item key={`skeleton-${rowIndex}-${colIndex}`} xs={12 / columnCount}>
                      <Skeleton variant="rectangular" height={estimateSize} sx={{ borderRadius: 5 }} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    )
  }

  // Render empty state
  if (items.length === 0 && noItemsLabel) {
    return (
      <Box className="WindowVirtualizedList" ref={containerRef} {...containerProps}>
        <Box className="WindowVirtualizedList__Status">{noItemsLabel}</Box>
      </Box>
    )
  }

  // Render virtualized items (use min height so container does not collapse when getTotalSize is 0)
  const virtualItems = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()
  const totalHeight = totalSize != null && totalSize > 0 ? `${totalSize}px` : '0px'

  return (
    <Box
      ref={containerRef}
      className="WindowVirtualizedList"
      {...containerProps}
      sx={{
        position: 'relative',
        height: totalHeight,
        ...containerProps?.sx,
      }}
    >
      {virtualItems.map((virtualRow: VirtualItem) => {
        const row = rows[virtualRow.index]
        if (!row) return null

        const rowStyle = {
          position: 'absolute' as const,
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${virtualRow.start - scrollMargin}px)`,
        }

        const rowNodes: ReactNode[] = row
          .map((item, colIndex) => {
            if (item == null) return null
            const itemIndex = virtualRow.index * columnCount + colIndex
            const key = getItemKey ? getItemKey(item, itemIndex) : `item-${itemIndex}`
            return (
              <Grid item key={key} xs={12 / columnCount}>
                {renderItem(item, itemIndex)}
              </Grid>
            )
          })
          .filter((n) => n != null) as ReactNode[]

        if (renderRow) {
          return (
            <Box key={virtualRow.key} className="WindowVirtualizedList__Row" ref={rowVirtualizer.measureElement} sx={rowStyle} data-index={virtualRow.index}>
              {renderRow(rowNodes, virtualRow.index)}
            </Box>
          )
        }

        return (
          <Grid
            container
            columnSpacing={gap / 8}
            key={virtualRow.key}
            className="WindowVirtualizedList__Row"
            ref={rowVirtualizer.measureElement}
            sx={rowStyle}
            data-index={virtualRow.index}
          >
            {rowNodes}
          </Grid>
        )
      })}
    </Box>
  )
}
